<script setup lang="ts">
import { TEButton, TETextInput, TETopLabelLayout } from '@ben/vite-te'
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import '@iframe-resizer/child'
import { Head } from '@inertiajs/vue3'
import axios from 'axios'

import type { Campaign, Participation } from '@/js/Types/laravel.ts'

const props = defineProps<{
  campaign: Campaign<'address_collection'>
}>()

const hasSigned = ref<boolean>(false)

const generalError = ref<string | null>(null)

function postMessageToParent(message: string) {
  if (window.parent) {
    window.parent.postMessage({ type: 'uff', data: message }, '*')
  }
}

function sign() {
  form.clearErrors()
  axios
    .post(route('campaign.participate', { campaign: props.campaign.id }), form.data())
    .then(() => {
      hasSigned.value = true
      postMessageToParent('signed')
    })
    .catch((error) => {
      console.error(error)
      if (error.response && error.response.status === 422) {
        form.errors = error.response.data.errors
        form.errors.first_name = error.response.data.errors.first_name?.[0]
        form.errors.last_name = error.response.data.errors.last_name?.[0]
        form.errors.email = error.response.data.errors.email?.[0]
        form.errors.zip = error.response.data.errors.zip?.[0]
        form.errors.address_street = error.response.data.errors.address_street?.[0]
        form.errors.address_house_number = error.response.data.errors.address_house_number?.[0]
        form.errors.city = error.response.data.errors.city?.[0]
        form.errors.address_collection_campaign_option_id =
          error.response.data.errors.address_collection_campaign_option_id?.[0]

        generalError.value = error.response.data.errors.general?.[0] || null
      }
    })
}

const form = useForm<Participation>({
  first_name: '',
  last_name: '',
  email: '',
  zip: '',
  address_collection_campaign_option_id: undefined,
  address_street: '',
  address_house_number: '',
  city: '',
  address_additional_info: '',
})
</script>

<template>
  <div class="mx-4 mt-4 max-w-5xl px-2 sm:mx-auto sm:px-0">
    <Head :title="campaign.name" />

    <main v-if="campaign.is_active" class="-mx-4 mb-4 sm:mx-4">
      <div class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4">
        <div v-auto-animate class="w-full">
          <h1 class="bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            {{ campaign.name }}
          </h1>

          <TransitionGroup
            name="list"
            tag="div"
            enter-active-class="transition transform duration-500 ease-out"
            leave-active-class="transition transform duration-500 ease-in"
            enter-from-class="translate-x-full opacity-0"
            enter-to-class="translate-x-0 opacity-100"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="-translate-x-full opacity-0"
          >
            <div class="py-2 text-xl font-semibold">Wähle deine {{ campaign.name }}-Variante:</div>

            <RadioGroup
              v-model="form.address_collection_campaign_option_id"
              :disabled="hasSigned"
              class="mt-2 grid w-full grid-cols-1 gap-3 sm:grid-cols-1"
            >
              <RadioGroupOption
                v-for="option in campaign.address_collection_campaign!.options"
                :key="option.id"
                v-slot="{ active, checked }"
                as="template"
                :value="option.id"
                :disabled="option.amount_left < 1"
              >
                <div
                  class="flex cursor-pointer items-center justify-center rounded-md p-3 text-sm font-semibold sm:flex-1"
                  :class="[
                    active ? 'ring-2 ring-primary-600 ring-offset-2' : '',
                    checked
                      ? 'bg-primary-600 text-white ring-0 hover:bg-primary-500'
                      : 'bg-white text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50',
                    !active && !checked ? 'ring-inset' : '',
                    active && checked ? 'ring-2' : '',
                  ]"
                >
                  <div class="flex flex-col items-center gap-1">
                    <div class="font-semibold">
                      {{ option.name }}
                    </div>

                    <div class="text-center font-normal">{{ option.description }}</div>
                  </div>
                </div>
              </RadioGroupOption>
            </RadioGroup>
          </TransitionGroup>
        </div>
      </div>

      <TransitionGroup
        name="list"
        tag="div"
        enter-active-class="transition transform duration-500 ease-out"
        leave-active-class="transition transform duration-500 ease-in"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="-translate-x-full opacity-0"
      >
        <div
          v-if="!hasSigned && form.address_collection_campaign_option_id !== undefined"
          class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4"
        >
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            Jetzt {{ campaign.name }}-Paket anfordern
          </div>

          <form class="w-full" @submit.prevent>
            <div class="w-full">
              <TETopLabelLayout
                class="col-span-2"
                title="Email"
                name="email"
                :error="form.errors.email"
                required
              >
                <TETextInput v-model="form.email" name="email" autocomplete="email" />
              </TETopLabelLayout>

              <div class="grid grid-cols-2 gap-2">
                <TETopLabelLayout
                  title="Vorname"
                  :error="form.errors.first_name"
                  name="first_name"
                  required
                >
                  <TETextInput
                    v-model="form.first_name"
                    name="first_name"
                    autocomplete="given-name"
                  />
                </TETopLabelLayout>

                <TETopLabelLayout
                  title="Nachname"
                  name="last_name"
                  :error="form.errors.last_name"
                  required
                >
                  <TETextInput
                    v-model="form.last_name"
                    name="last_name"
                    autocomplete="family-name"
                  />
                </TETopLabelLayout>
              </div>

              <div class="grid grid-cols-3 gap-2">
                <TETopLabelLayout
                  title="Straße"
                  class="col-span-2"
                  name="address_street"
                  required
                  :error="form.errors.address_street"
                >
                  <TETextInput
                    v-model="form.address_street"
                    autocomplete="address-line1"
                    name="address_street"
                  />
                </TETopLabelLayout>

                <TETopLabelLayout
                  title="Nr."
                  name="address_house_number"
                  required
                  :error="form.errors.address_house_number"
                >
                  <TETextInput
                    v-model="form.address_house_number"
                    autocomplete="address-line2"
                    name="address_house_number"
                  />
                </TETopLabelLayout>
              </div>

              <div class="grid grid-cols-3 gap-2">
                <TETopLabelLayout title="Postleitzahl" name="zip" required :error="form.errors.zip">
                  <TETextInput v-model="form.zip" autocomplete="postal-code" name="zip" />
                </TETopLabelLayout>

                <TETopLabelLayout
                  title="Stadt"
                  class="col-span-2"
                  name="city"
                  required
                  :error="form.errors.city"
                >
                  <TETextInput v-model="form.city" autocomplete="address-level2" name="city" />
                </TETopLabelLayout>
              </div>
            </div>

            <div class="col-span-2 mb-2">
              <span class="text-sm text-gray-500"
                >Ich bin damit einverstanden, dass meine E-Mail-Adresse, Name und Ort verarbeitet
                werden. Meine Adresse wird für den Versand des Mobi-Pakets verwendet. Zur
                Verifizierung der Daten erhalte ich eine E-Mail. Diese Einwilligung kann jederzeit
                widerrufen werden.
                <a href="https://fridaysforfuture.de/datenschutz" class="underline" target="_blank"
                  >Mehr Infos</a
                ></span
              >
            </div>

            <TEAlert v-if="generalError" class="my-2" type="error" :title="generalError" />

            <TEButton id="sign" type="button" class="w-full !rounded-full" primary @click="sign"
              ><span class="-m-2 text-xl font-bold uppercase">Paket KOSTENLOS anfordern</span>
            </TEButton>
          </form>
        </div>

        <div v-if="hasSigned" class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4">
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            Noch ein Schritt fehlt - jetzt E-Mail bestätigen!
          </div>

          <div>
            <p class="mt-4">
              Rufe deine E-Mail jetzt ab und klicke auf den Bestätigungslink. Nur dann können wir
              sicher sein, dass du kein Bot bist und dein Mobi-Paket versenden.
            </p>

            <p v-if="campaign.email_footer" class="whitespace-pre-wrap">
              {{ campaign.email_footer }}
            </p>
          </div>
        </div>
      </TransitionGroup>
    </main>

    <div v-else class="box my-4 rounded-3xl bg-white p-6">
      <h1 class="text-2xl font-bold">Diese Aktion ist leider nicht mehr aktiv.</h1>

      <p class="mt-4">
        Diese Aktion ist leider nicht mehr aktiv. Schau dich gerne auf
        <a href="https://fridaysforfuture.de/" class="underline" target="_blank"
          >fridaysforfuture.de</a
        >
        um, dort findest du unsere aktuellen Kampagnen und Aktionen.
      </p>
    </div>
  </div>
</template>

<style>
.box {
  box-shadow: 1px 3px 5px 0 rgba(0, 0, 0, 0.3);
}

li {
  list-style-type: disc;
  margin-left: 2rem;
}

p + p {
  margin-top: 1rem;
}

body {
  @apply !bg-white;
}
</style>
